import { useLayoutEffect, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { PREVIEW_OFFSET_VAR } from "@/lib/previewBannerOffset";

/**
 * The owner's draft-preview notice on the public page. It stays pinned to the top
 * of the viewport (the floating navs are pinned too, so a banner that scrolled
 * away would leave nothing telling the owner this is a draft), and a spacer of the
 * same height keeps the page content from starting underneath it. Its height is
 * published as PREVIEW_OFFSET_VAR so the floating navs sit below it (JUB-154).
 */
export function PreviewDraftBanner() {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const root = document.documentElement;
    const apply = () => {
      const next = Math.ceil(node.getBoundingClientRect().height);
      setHeight(next);
      root.style.setProperty(PREVIEW_OFFSET_VAR, `${next}px`);
    };
    apply();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(apply);
    observer?.observe(node);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(PREVIEW_OFFSET_VAR);
    };
  }, []);

  return (
    <>
      <div
        ref={ref}
        role="status"
        data-testid="preview-draft-banner"
        // Below the dialog layer (z-50) so an open RSVP/gift dialog is never covered;
        // the navs cannot overlap it because they are offset by its height.
        className="fixed inset-x-0 top-0 z-[45] bg-amber-100 text-amber-900"
      >
        <div className="container mx-auto flex items-center gap-2 px-4 py-3 text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{t("events.form.previewDraftNotice")}</span>
        </div>
      </div>
      <div aria-hidden="true" style={{ height }} />
    </>
  );
}
